import { useState, useRef, useCallback } from 'react';
import RotaryNavButton from './RotaryNavButton';

interface Project {
  title: string;
  category: string;
  description: string;
  image: string;
  youtubeUrl?: string;
  url?: string;
  note?: string;
}

const PUBLIC_PROJECTS: Project[] = [
  {
    title: '勝尾寺 語り （浜村 淳様）',
    category: 'ナレーション収録 / MA',
    description: '勝尾寺の歴史と魅力を浜村淳様の語りで表現した作品',
    image: '/images/works/works-1.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=ht-KfojF9tg',
  },
  {
    title: '勝尾寺 語り 2025年 （窪田 等様）',
    category: 'ナレーション収録 / MA',
    description: '2025年版として窪田等様による勝尾寺の新たな語り',
    image: '/images/works/works-2.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=iUGPrdVNaog',
  },
  {
    title: '勝尾寺縁起 （浜村 淳様）',
    category: 'ナレーション収録 / MA',
    description: '勝尾寺の由来と縁起を浜村淳様の語りで紹介',
    image: '/images/works/works-3.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=FyFn6IDPxqQ',
  },
  {
    title: '「MAスタジオに行こう」',
    category: '取材 / ドキュメンタリー',
    description: 'ガリレオクラブ時代に取材を受けました。スタジオってどんなとこ？',
    image: '/images/backgrounds/ma-studio-galileo.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=drvHy-s24nA&t=1s',
  },
  {
    title: '「icho2021」国際科学オリンピック リモート大会',
    category: 'MA / 音響効果 / 出張録音',
    description: '国際科学オリンピックのリモート大会における音響制作と録音を担当',
    image: '/images/works/works-4.jpg',
    url: 'https://www.icho2021.org/jp/video/',
  },
  {
    title: '2025大阪関西万博 関西パビリオン 福井県「恐竜王国360°映像」',
    category: 'MA　空間音響デザイン　音響効果',
    description: '大阪関西万博のパビリオンに参加し、お客様のイメージを具現化するお手伝いをしました。国際的な舞台での音響制作経験を活かし、訪問者に感動的な体験を提供しました。',
    image: '/images/backgrounds/expo2025-fukui-dinosaur.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=6QkSsRyhmhY',
    note: '※実際の映像は権利の問題で公開できません',
  },
];

export default function WorksSlider() {
  const [index, setIndex] = useState(0);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  const total = PUBLIC_PROJECTS.length;
  const project = PUBLIC_PROJECTS[index];

  const go = useCallback((dir: number) => {
    setIndex((i) => (i + dir + total) % total);
  }, [total]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const handleMouseLeave = useCallback(() => setCursor(null), []);

  return (
    <div className="relative">
      {/* 左右の丸型回転ナビボタン */}
      <div className="absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 z-20">
        <RotaryNavButton direction="prev" label="Prev" onClick={() => go(-1)} />
      </div>
      <div className="absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 z-20">
        <RotaryNavButton direction="next" label="Next" onClick={() => go(1)} />
      </div>

      {/* 現在のプロジェクト */}
      <div className="max-w-3xl mx-auto px-16 md:px-20">
        <div
          ref={imageRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="relative rounded-xl overflow-hidden border border-white/10 group"
        >
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-64 md:h-80 object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          {/* ホバー時にカーソル位置へ浮かぶ回転ボタン */}
          {cursor && (
            <button
              type="button"
              onClick={() => go(1)}
              className="absolute -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full border border-white/20 bg-black/70 backdrop-blur-md text-white flex flex-col items-center justify-center gap-1 cursor-pointer whitespace-nowrap hover:border-teal-300 transition-colors pointer-events-auto"
              style={{ left: cursor.x, top: cursor.y }}
              aria-label="次の作品へ"
            >
              <span className="absolute inset-0 rounded-full border-2 border-dashed border-teal-400/60 rotary-spin-slow pointer-events-none" />
              <i className="ri-arrow-right-line text-xl text-teal-300" />
              <span className="text-[9px] tracking-[0.2em] uppercase text-white/70">Next</span>
            </button>
          )}
        </div>

        <div className="pt-6 text-center">
          <div className="text-sm text-teal-300 font-semibold mb-2 tracking-wide">{project.category}</div>
          <h4 className="text-xl md:text-2xl font-mincho text-white mb-3">{project.title}</h4>
          <p className="text-zinc-400 text-sm leading-relaxed mb-3">{project.description}</p>
          {project.note && (
            <p className="text-xs text-zinc-500 italic mb-3">{project.note}</p>
          )}
          <div className="flex flex-wrap gap-4 justify-center">
            {project.youtubeUrl && (
              <button
                className="flex items-center text-teal-300 hover:text-teal-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                onClick={() => window.open(project.youtubeUrl, '_blank')}
              >
                <i className="ri-youtube-fill mr-2"></i>
                YouTubeで視聴
              </button>
            )}
            {project.url && (
              <button
                className="flex items-center text-teal-300 hover:text-teal-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                onClick={() => window.open(project.url, '_blank')}
              >
                <i className="ri-external-link-line mr-2"></i>
                サイトで確認
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ドットインジケーター */}
      <div className="flex items-center justify-center gap-2 mt-6">
        {PUBLIC_PROJECTS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`${i + 1}件目を表示`}
            className={`h-2 rounded-full transition-all cursor-pointer ${i === index ? 'w-6 bg-teal-400' : 'w-2 bg-white/20 hover:bg-white/40'}`}
          />
        ))}
      </div>

      <p className="text-center text-xs text-zinc-500 mt-3 tracking-widest">
        {index + 1} / {total}
      </p>
    </div>
  );
}